import { useCallback } from 'react';
import { useDataProvider } from 'react-admin';

import { useLogoImage } from '@app/hooks/useLogoImage';
import { useOrderable } from '@app/hooks/useOrderable';
import {
  FIELD_TYPES,
  formatNonRequiredFields,
  validateConditionalFields,
  validateFormFields,
} from '@app/utils/validateUtils';

import { DeleteConfirmation, GenericObject } from '@app/components/common/Table/DefaultTable/types';
import EditView from '@app/components/common/views/EditView';

import { CTA_TARGET_TYPE, getCTAPayload } from '.';
import { CTAForm } from './CTAForm';

const formFields = [
  { key: 'heading', type: FIELD_TYPES.MULTILINGUAL, message: 'Heading is required' },
  { key: 'name', type: FIELD_TYPES.MULTILINGUAL, message: 'Name is required' },
  { key: 'subject', type: FIELD_TYPES.MULTILINGUAL, message: 'Subject is required' },
  { key: 'target_type', type: FIELD_TYPES.TEXT, message: 'Target type is required' },
];

const conditionalFields = [
  {
    key: 'external_link_url',
    type: FIELD_TYPES.TEXT,
    message: 'URL is required',
    shouldValidate: (values: GenericObject) => values.target_type === 'EXTERNAL_LINK',
  },
];

const nonRequiredFields = [
  { key: 'description', emptyValue: '', isMultilingual: true },
  { key: 'sub_heading', emptyValue: '', isMultilingual: true },
  { key: 'related_topic_id', emptyValue: null },
];

export const EditCTA = () => {
  const dataProvider = useDataProvider();
  const { logoImage, handleChangeImageLogo } = useLogoImage();

  const { selected, handleChangeSelected, moveCard } = useOrderable();

  const validate = useCallback(
    (values: GenericObject) => {
      let mediaErrors = {};
      const errors = validateFormFields({ fields: formFields, values });
      const conditionalFieldsErrors = validateConditionalFields({ fields: conditionalFields, values });
      if (values.target_type === CTA_TARGET_TYPE.MEDIA) {
        if (!selected.length) {
          mediaErrors = { media: 'Media is required' };
        }
      }

      return { ...errors, ...conditionalFieldsErrors, ...mediaErrors };
    },
    [selected]
  );

  const transform = (values: GenericObject) => {
    formatNonRequiredFields(values, nonRequiredFields);

    return getCTAPayload(values, logoImage, selected);
  };

  const resolveDeleteConfirmation = async (ctaId: number): Promise<DeleteConfirmation> => {
    const response = await dataProvider.fetchLinks('ctas', ctaId);
    const links = response?.data;
    if (links.Rails > 0) {
      return {
        deletable: false,
        message: `This CTA is associated with ${links.Rails} rail(s). Remove the CTA from all rails and try again.`,
      };
    }

    return { deletable: true };
  };

  return (
    <div>
      <EditView
        resolveDeleteConfirmation={resolveDeleteConfirmation}
        resourceSingular="cta"
        validate={validate}
        transform={transform}
        mutationMode="pessimistic">
        <CTAForm
          ctaLogo={logoImage}
          selectedMedia={selected}
          moveCard={moveCard}
          handleChangeSelectedMedia={handleChangeSelected}
          title="EDIT CTA"
          handleChangeCtaLogo={handleChangeImageLogo}
        />
      </EditView>
    </div>
  );
};
